import { ArrowLeft, Ban, CheckCircle2, Info, Send, ShieldAlert } from "lucide-react";
import { type FormEvent, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { CustomerGate } from "../../components/AuthGate";
import { parseCc } from "../../components/Composer";
import { FilePicker } from "../../components/FilePicker";
import { RichTextEditor } from "../../components/RichTextEditor";
import { useSession } from "../../context/SessionContext";
import { useToast } from "../../context/ToastContext";
import { useSubmissionAttempt, createSubmissionSignature } from "../../hooks/useSubmissionAttempt";
import { apiRequest, uploadAttachments } from "../../lib/api";
import type { Ticket } from "../../lib/types";
import { customerTicketPath } from "../../lib/ticketLinks";
import { customerTicketValidation, CUSTOMER_SUBJECT_MIN, CUSTOMER_SUBJECT_MAX, CUSTOMER_DETAILS_MIN, CUSTOMER_DETAILS_MAX } from "../../lib/customerTicketValidation";

function NewTicketForm() {
  const { config, user } = useSession();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [subject, setSubject] = useState(searchParams.get("subject") ?? "");
  const [category, setCategory] = useState("migration_issue");
  const [migrationId, setMigrationId] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionHtml, setDescriptionHtml] = useState("");
  const [cc, setCc] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(true);
  const inFlightRef = useRef(false);
  const submission = useSubmissionAttempt("customer-ticket");
  const validation = customerTicketValidation(subject, description);
  const canSubmit = validation.valid && accepted;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit || busy || inFlightRef.current) return;
    const draft = {
      subject: subject.trim(),
      description: description.trim(),
      descriptionHtml,
      category,
      priority: "normal",
      context: migrationId.trim() ? { migrationId: migrationId.trim() } : undefined,
      cc: parseCc(cc),
    };
    const attemptKey = submission.keyFor(createSubmissionSignature(draft, files));
    inFlightRef.current = true;
    setBusy(true);
    try {
      const attachments = await uploadAttachments(files, attemptKey);
      const result = await apiRequest<Ticket | { ticket: Ticket }>("/tickets", {
        method: "POST",
        idempotencyKey: attemptKey,
        body: {
          ...draft,
          attachmentIds: attachments.map((attachment) => attachment.id),
        },
      });
      const ticket = "ticket" in result ? result.ticket : result;
      submission.reset(attemptKey);
      navigate(customerTicketPath(ticket), { replace: true, state: { ticketSubmittedId: ticket.id } });
    } catch (requestError) {
      showToast(requestError instanceof Error ? requestError.message : "Ticket creation failed.", "error");
    } finally {
      inFlightRef.current = false;
      setBusy(false);
    }
  }

  if (user?.kind === "customer" && user.ticketCreationBlocked) return (
    <section className="new-ticket-layout new-ticket-layout--restricted">
      <div className="card restricted-customer-notice"><Ban aria-hidden="true" /><div><p className="eyebrow">Account restricted</p><h1>You cannot create a new ticket</h1><p>Your support account has been restricted. You can still sign in and review your existing ticket history, but new tickets, replies, and uploads are unavailable.</p><Link className="button button--secondary" to="/tickets">View existing tickets</Link></div></div>
    </section>
  );

  return (
    <section className="new-ticket-layout">
      <div className="new-ticket-main">
        <Link className="back-link" to="/tickets"><ArrowLeft aria-hidden="true" size={16} /> Back to tickets</Link>
        <header className="page-header page-header--compact"><div><p className="eyebrow">New request</p><h1>Tell us what is happening</h1><p>Clear details help us get you to the right specialist faster.</p></div></header>
        {user?.kind === "customer" && user.spamMarked && <div className="settings-callout settings-callout--warning customer-spam-notice"><ShieldAlert aria-hidden="true" /><p><strong>Your requests are under spam review</strong>New tickets will be created in Spam &amp; quarantine. They remain visible in your account while our support team reviews them.</p></div>}
        <form className="ticket-form card" onSubmit={submit}>
          <label className="field">
            <span>What do you need help with? <i>*</i></span>
            <input value={subject} onChange={(event) => setSubject(event.target.value)} required minLength={CUSTOMER_SUBJECT_MIN} maxLength={CUSTOMER_SUBJECT_MAX} aria-describedby="ticket-subject-help" aria-invalid={!validation.subjectValid} placeholder="A short summary of the issue" autoFocus />
            <small id="ticket-subject-help"><span className={!validation.subjectValid ? "field-character-count--invalid" : undefined}>{validation.subjectLength}</span>/{CUSTOMER_SUBJECT_MAX} · Minimum {CUSTOMER_SUBJECT_MIN} characters. Leading and trailing spaces do not count.</small>
          </label>
          <div className="form-grid">
            <label className="field"><span>Topic</span><select value={category} onChange={(event) => setCategory(event.target.value)}><option value="migration_issue">Migration issue</option><option value="pre_sale">Pre-sale question</option><option value="billing">Billing & payment</option><option value="technical">Technical integration</option><option value="account">Account</option><option value="other">Other</option></select></label>
            <label className="field"><span>Migration or order ID <em>Optional</em></span><input value={migrationId} onChange={(event) => setMigrationId(event.target.value)} placeholder="e.g. 15687" /></label>
          </div>
          <label className="field"><span>Cc <em>Optional</em></span><input type="text" inputMode="email" value={cc} onChange={(event) => setCc(event.target.value)} placeholder="name@example.com, another@example.com" /><small>These people receive this initial message. Choose Cc separately for later replies.</small></label>
          <div className="field"><span>Details <i>*</i></span><RichTextEditor html={descriptionHtml} onChange={(html, text) => { setDescriptionHtml(html); setDescription(text); }} placeholder="What did you expect, what happened instead, and when did you first notice it?" ariaLabel="Ticket details" ariaDescribedBy="ticket-details-help" ariaInvalid={!validation.detailsValid} ariaRequired disabled={busy} /><small id="ticket-details-help"><span className={!validation.detailsValid ? "field-character-count--invalid" : undefined}>{validation.detailsLength}</span> characters · Minimum {CUSTOMER_DETAILS_MIN}; maximum {CUSTOMER_DETAILS_MAX.toLocaleString("en-US")}. Leading and trailing spaces do not count. Never include passwords, card details, or secret API keys.</small></div>
          <div className="field"><span>Attachments <em>Optional</em></span><FilePicker files={files} onChange={setFiles} maxBytes={config.maxAttachmentBytes} disabled={busy} /></div>
          <label className="check-field"><input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} /><span>I confirm this request does not contain passwords or payment card details.</span></label>
          <div className="ticket-form__footer"><p><Info aria-hidden="true" size={15} /> Replies will also be sent to your Next-Cart account email.</p><button className="button button--primary" type="submit" disabled={!canSubmit || busy}><Send aria-hidden="true" size={17} /> {busy ? "Creating…" : "Create ticket"}</button></div>
        </form>
      </div>
      <aside className="new-ticket-help card">
        <h2>For a faster answer</h2>
        <ul>
          <li><CheckCircle2 aria-hidden="true" /><span><strong>Add the migration ID</strong>We can pull the right logs immediately.</span></li>
          <li><CheckCircle2 aria-hidden="true" /><span><strong>Share exact examples</strong>Include a product URL, order ID, or screenshot.</span></li>
          <li><CheckCircle2 aria-hidden="true" /><span><strong>Describe recent changes</strong>Tell us what changed before the issue began.</span></li>
        </ul>
        <p>Most requests receive a first response during support hours.</p>
      </aside>
    </section>
  );
}

export function NewTicketPage() {
  return <CustomerGate><NewTicketForm /></CustomerGate>;
}
